import React from 'react'
import PropTypes from 'prop-types'
import TablePagination from '@mui/material/TablePagination'
import { IconButton, useTheme, useMediaQuery } from '@mui/material'
import FirstPageIcon from '@mui/icons-material/FirstPage'
import LastPageIcon from '@mui/icons-material/LastPage'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import { useRecoilValue } from 'recoil'
import { userState } from '../../../states/userState'
import { foreColorCodePagination } from '../../../commons/colors'
import classes from './CustomTablePagination.module.scss'
import TextInputWithNoSearch from './textFields/TextInputWithNoSearch'

// カスタムページネーションアクションコンポーネント
function TablePaginationActions(props) {
  const theme = useTheme()
  const matches = useMediaQuery(theme.breakpoints.down('sm'))
  const iconButtonStyle = matches
    ? { padding: '1px' }
    : { marginLeft: theme.spacing(1.5) }
  const { count, page, rowsPerPage, onPageChange } = props

  // データがあるかどうかを判定
  const hasData = count > 0

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0)
  }

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1)
  }

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1)
  }

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1))
  }

  return (
    <div
      style={{
        flexShrink: 0,
        marginLeft: theme.spacing(1.0),
        marginRight: theme.spacing(1.5),
      }}
    >
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
        style={iconButtonStyle}
        color={hasData ? 'primary' : 'default'}
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
        style={iconButtonStyle}
        color={hasData ? 'primary' : 'default'}
      >
        {theme.direction === 'rtl' ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
        style={iconButtonStyle}
        color={hasData ? 'primary' : 'default'}
      >
        {theme.direction === 'rtl' ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
        style={iconButtonStyle}
        color={hasData ? 'primary' : 'default'}
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </div>
  )
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
}

const CustomTablePagination = ({
  count,
  page,
  onPageChange,
  rowsPerPage,
  onRowsPerPageChange,
  setKeywords,
  keywords,
  setKintoneArray,
}) => {
  const theme = useTheme()
  const user = useRecoilValue(userState)

  const matches = useMediaQuery(theme.breakpoints.down('sm'))
  // 日本語のラベル設定
  const labelDisplayedRows = ({ from, to, count }) =>
    `${from}-${to} 件 / ${count !== -1 ? count : `more than ${to}`}件`
  const labelRowsPerPage = 'ページあたりの行数:'

  const customPaginationStyle = matches
    ? {
        '.css-16c50h-MuiInputBase-root-MuiTablePagination-select': {
          margin: 0,
        },
        '.css-194a1fa-MuiSelect-select-MuiInputBase-input.css-194a1fa-MuiSelect-select-MuiInputBase-input.css-194a1fa-MuiSelect-select-MuiInputBase-input':
          {
            paddingRight: '12px',
          },
        '.MuiTablePagination-toolbar': {
          padding: theme.spacing(1), // 画面幅がmd以下の場合のpadding調整
          color: foreColorCodePagination(user?.mode ?? 'lite'),
          '&:hover': {
            color: '#ff0', // ホバー時の色
          },
          '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows':
            {
              fontSize: '12px', // フォントサイズを小さくする
              color: foreColorCodePagination(user?.mode ?? 'lite'),
              '&:hover': {
                color: '#ff0', // ホバー時の色
              },
            },
          '.MuiTablePagination-select, .MuiTablePagination-selectIcon': {
            fontSize: '12px', // セレクトボックスのフォントサイズを小さくする
            color: foreColorCodePagination(user?.mode ?? 'lite'),
            '&:hover': {
              color: '#ff0', // ホバー時の色
            },
          },
          '.MuiTablePagination-actions': {
            marginRight: theme.spacing(0), // アクションボタンの右マージンを調整
            color: foreColorCodePagination(user?.mode ?? 'lite'),
            '&:hover': {
              color: '#ff0', // ホバー時の色
            },
          },
        },
      }
    : {
        '.MuiTablePagination-toolbar': {
          //表示数のカラー
          color: foreColorCodePagination(user?.mode ?? 'lite'),
          '&:hover': {
            color: '#ff0', // ホバー時の色
          },
        },
        '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
          //ページ当たりの行数　xx / xx　のカラー
          color: foreColorCodePagination(user?.mode ?? 'lite'),
          '&:hover': {
            color: '#ff0', // ホバー時の色
          },
        },
      }
  return (
    <div className={classes.root}>
      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[20, 50, 100]}
        onRowsPerPageChange={onRowsPerPageChange}
        ActionsComponent={TablePaginationActions}
        labelDisplayedRows={labelDisplayedRows}
        labelRowsPerPage={labelRowsPerPage}
        sx={customPaginationStyle}
        // ActionsComponentをカスタマイズすることも可能です
      />
      <div className={classes.keywords}>
        <TextInputWithNoSearch
          fullWidth={false}
          label={'キーワードを含む'}
          multiline={false}
          required={false}
          onChange={(e) => setKeywords(e.target.value)}
          minRows={1}
          value={keywords}
          type={'text'}
          InputProps={true}
          setKintoneArray={setKintoneArray}
        />
      </div>
    </div>
  )
}

export default CustomTablePagination
